import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Building2, Users, Zap, CheckCircle, Mail, ArrowRight, BookOpen, User, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const Institutionen = () => {
  const navigate = useNavigate();
  const { user, showAuthModal } = useAuth();
  
  const [formData, setFormData] = useState({
    institution: '',
    contactPerson: '',
    email: '',
    phone: '',
    clientCount: '',
    institutionType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      showAuthModal('register');
    }
  };

  const handleSignIn = () => {
    showAuthModal('login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formspreeUrl = import.meta.env.VITE_FORMSPREE_URL;
      
      if (!formspreeUrl) {
        throw new Error('Formspree URL nicht konfiguriert');
      }

      // Validierung
      if (!formData.institution || !formData.contactPerson || !formData.email || !formData.clientCount || !formData.institutionType) {
        toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
        setIsSubmitting(false);
        return;
      }
      
      const submitData = new FormData();
      submitData.append('_subject', 'Institutionen-Anfrage - KaufPause Gruppenlizenz');
      submitData.append('_replyto', formData.email);
      submitData.append('institution', formData.institution);
      submitData.append('contactPerson', formData.contactPerson);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone || 'Nicht angegeben');
      submitData.append('clientCount', formData.clientCount);
      submitData.append('institutionType', formData.institutionType);
      submitData.append('message', formData.message || 'Keine zusätzliche Nachricht');
      
      const response = await fetch(formspreeUrl, {
        method: 'POST',
        body: submitData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Anfrage erfolgreich gesendet!', {
          description: 'Wir melden uns innerhalb von 24 Stunden bei Ihnen.',
          duration: 6000,
        });
        
        // Form zurücksetzen
        setFormData({
          institution: '',
          contactPerson: '',
          email: '',
          phone: '',
          clientCount: '',
          institutionType: '',
          message: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Formspree Error:', errorData);
        throw new Error('Fehler beim Senden der Anfrage');
      }
    } catch (error) {
      console.error('Submit Error:', error);
      toast.error('Fehler beim Senden der Anfrage', {
        description: 'Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter kontakt@kaufpause.at',
        duration: 8000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const institutionTypes = [
    'Schuldnerberatung',
    'Verbraucherzentrale',
    'Sozialberatung',
    'Caritas/Diakonie',
    'Jobcenter',
    'Bildungseinrichtung',
    'Therapeutische Praxis',
    'Sonstige'
  ];

  const features = [
    {
      icon: Users,
      title: 'Flexible Gruppenlizenzen',
      description: 'Erwerben Sie Zugangskeys für Ihre Klienten in beliebiger Anzahl - von 10 bis 1000+ Lizenzen.'
    },
    {
      icon: Building2,
      title: 'Zentrale Verwaltung',
      description: 'Admin-Dashboard zur Verwaltung aller Klienten-Accounts und Übersicht über die Nutzung.'
    },
    {
      icon: Zap,
      title: 'Individuelle Anpassungen',
      description: 'Maßgeschneiderte Funktionen und Features speziell für Ihre institutionellen Anforderungen.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Gruppenlizenzen für Institutionen | KaufPause - Bewusster Konsum für Ihre Klienten</title>
        <meta name="description" content="Flexible Gruppenlizenzen für Schuldnerberatungen, Verbraucherzentralen und soziale Einrichtungen. KaufPause hilft Ihren Klienten beim bewussten Konsum." />
        <meta name="keywords" content="Gruppenlizenz, Schuldnerberatung, Verbraucherzentrale, bewusster Konsum, Sozialberatung" />
        <link rel="canonical" href="https://kaufpause.at/institutionen" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Gruppenlizenzen für Institutionen | KaufPause" />
        <meta property="og:description" content="Flexible Gruppenlizenzen für Schuldnerberatungen und soziale Einrichtungen. Helfen Sie Ihren Klienten beim bewussten Konsum." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kaufpause.at/institutionen" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "KaufPause Gruppenlizenzen",
            "description": "Flexible Gruppenlizenzen für Institutionen zur Förderung bewussten Konsums",
            "provider": {
              "@type": "Organization",
              "name": "KaufPause"
            },
            "serviceType": "Software-Lizenzierung",
            "areaServed": "Deutschland",
            "audience": {
              "@type": "Audience",
              "audienceType": "Schuldnerberatungen, Verbraucherzentralen, soziale Einrichtungen"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <a 
                href="/" 
                className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                <img 
                  src="/Favicon/favicon-196x196.png" 
                  alt="KaufPause Logo" 
                  className="w-8 h-8 -translate-y-0.5"
                />
                <span className="text-xl font-bold text-gray-900">
                  Kauf<span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">Pause</span>
                </span>
              </a>
            </div>
            
            <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <button
                onClick={() => navigate('/blog')}
                className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Blog
              </button>
              <button
                onClick={() => navigate('/institutionen')}
                className="text-emerald-600 bg-emerald-50 transition-colors duration-200 font-medium px-3 py-2 rounded-lg flex items-center gap-2"
              >
                <Building2 className="w-4 h-4" />
                Für Institutionen
              </button>
              {!user && (
                <button
                  onClick={() => navigate('/demo')}
                  className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
                >
                  <PlayCircle className="w-4 h-4" />
                  Demo testen
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-600 hover:text-gray-800 hidden sm:flex"
                  >
                    Abmelden
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                    onClick={handleGetStarted}
                  >
                    Dashboard
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className="text-gray-600 hover:text-emerald-600 hidden sm:flex"
                    onClick={handleSignIn}
                  >
                    Anmelden
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                    onClick={handleGetStarted}
                  >
                    Kostenlos starten
                  </Button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-16 px-4 relative overflow-hidden">
          {/* Subtle background decoration */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-20 left-20 w-32 h-32 bg-emerald-100/30 rounded-full blur-3xl"></div>
            <div className="absolute top-40 right-20 w-40 h-40 bg-blue-100/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-1/3 w-36 h-36 bg-purple-100/20 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Gruppenlizenzen für{' '}
                <span className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-600 bg-clip-text text-transparent">
                  Institutionen
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto leading-relaxed">
                Helfen Sie Ihren Klienten beim bewussten Konsumieren. Flexible Gruppenlizenzen für Schuldnerberatungen, Verbraucherzentralen und soziale Einrichtungen.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 max-w-2xl mx-auto">
                <p className="text-sm text-blue-800 text-center">
                  <strong>Preismodell:</strong> Günstige Einzellizenzen + einmalige Aktivierungskosten + monatliche Admin Dashboard Gebühr für zentrale Verwaltung
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg"
                  onClick={() => document.getElementById('kontakt-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group rounded-xl"
                >
                  Jetzt anfragen
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/')}
                  className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-4 text-lg transition-all duration-300 hover:scale-105 flex items-center rounded-xl"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Mehr erfahren
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Speziell für Ihre Bedürfnisse
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Maßgeschneiderte Lösungen für professionelle Beratungseinrichtungen
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <CardHeader className="text-center p-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors duration-300">
                      <feature.icon className="w-8 h-8 text-emerald-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-8 pb-8">
                    <p className="text-gray-600 text-center leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="kontakt-form" className="py-20 px-4 bg-gradient-to-br from-gray-50/30 via-white to-emerald-50/30">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Kostenloses Angebot anfordern
              </h2>
              <p className="text-xl text-gray-600">
                Lassen Sie uns über Ihre Anforderungen sprechen
              </p>
            </div>

            <Card className="border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="institution" className="text-sm font-medium text-gray-700">
                        Institution/Organisation *
                      </Label>
                      <Input
                        id="institution"
                        type="text"
                        value={formData.institution}
                        onChange={(e) => handleInputChange('institution', e.target.value)}
                        placeholder="z.B. Schuldnerberatung München"
                        className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contactPerson" className="text-sm font-medium text-gray-700">
                        Ansprechpartner *
                      </Label>
                      <Input
                        id="contactPerson"
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                        placeholder="Ihr Name"
                        className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                        E-Mail-Adresse *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="ihre.email@institution.de"
                        className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                        Telefon (optional)
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+49 123 456789"
                        className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="institutionType" className="text-sm font-medium text-gray-700">
                        Art der Institution *
                      </Label>
                      <Select value={formData.institutionType} onValueChange={(value) => handleInputChange('institutionType', value)}>
                        <SelectTrigger className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500">
                          <SelectValue placeholder="Wählen Sie eine Option" />
                        </SelectTrigger>
                        <SelectContent>
                          {institutionTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="clientCount" className="text-sm font-medium text-gray-700">
                        Anzahl Klienten/Nutzer *
                      </Label>
                      <Select value={formData.clientCount} onValueChange={(value) => handleInputChange('clientCount', value)}>
                        <SelectTrigger className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500">
                          <SelectValue placeholder="Geschätzte Anzahl" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10-25">10-25 Nutzer</SelectItem>
                          <SelectItem value="26-50">26-50 Nutzer</SelectItem>
                          <SelectItem value="51-100">51-100 Nutzer</SelectItem>
                          <SelectItem value="101-250">101-250 Nutzer</SelectItem>
                          <SelectItem value="251-500">251-500 Nutzer</SelectItem>
                          <SelectItem value="500+">Mehr als 500 Nutzer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                      Zusätzliche Nachricht (optional)
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Teilen Sie uns Ihre spezifischen Anforderungen oder Fragen mit..."
                      rows={4}
                      className="rounded-xl border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex justify-center pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      size="lg"
                      className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-12 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 rounded-xl"
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
                      {!isSubmitting && <Mail className="ml-2 h-5 w-5" />}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Warum KaufPause für Ihre Institution?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Bewährte Tools für nachhaltigen Erfolg in der Beratungsarbeit
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Nachhaltige Verhaltensänderung</h3>
                    <p className="text-gray-600">30-Tage-Bedenkzeit hilft Klienten, Impulskäufe zu reflektieren und bewusstere Entscheidungen zu treffen.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Einfache Integration</h3>
                    <p className="text-gray-600">Schnelle Einrichtung ohne technische Hürden. Ihre Klienten können sofort loslegen.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Datenschutz & Sicherheit</h3>
                    <p className="text-gray-600">DSGVO-konform gehostet in Deutschland. Keine Weitergabe persönlicher Daten.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Kosteneffizient</h3>
                    <p className="text-gray-600">Günstige Gruppenpreise ab 10 Lizenzen. Deutliche Ersparnisse gegenüber Einzellizenzen.</p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8 border border-emerald-100">
                  <div className="text-center space-y-4">
                    <Building2 className="w-16 h-16 text-emerald-600 mx-auto" />
                    <h3 className="text-2xl font-bold text-gray-900">Starten Sie noch heute</h3>
                    <p className="text-gray-600">
                      Unverbindliches Angebot innerhalb von 24 Stunden
                    </p>
                    <Button
                      onClick={() => document.getElementById('kontakt-form')?.scrollIntoView({ behavior: 'smooth' })}
                      className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Kostenlos anfragen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-emerald-600 via-emerald-700 to-green-600 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-lg"></div>
          </div>

          <div className="container mx-auto max-w-4xl relative z-10 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Bereit für den nächsten Schritt?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Helfen Sie Ihren Klienten dabei, bewusstere Kaufentscheidungen zu treffen und langfristig ihre finanzielle Situation zu verbessern.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={() => document.getElementById('kontakt-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-emerald-700 hover:bg-gray-50 px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 rounded-xl font-semibold"
              >
                Jetzt Angebot anfordern
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-emerald-100 text-sm">
                ✓ Kostenlose Beratung ✓ Unverbindlich ✓ 24h Antwortzeit
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Institutionen; 